import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, DialogContent, Typography } from '@mui/material';
import { DialogStep } from '../ManageSubscriptionDialog';
import DialogActions from '../../common/DialogActions';
import CancelIcon from '../../../assets/subscription/cancel.svg';
import { notifyBugsnagHandledError } from '../../../utils/bugsnagClient';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import SettingItem from '../../SettingItem';
import { campaign, manageSubscriptionSteps, subscriptionDateFormat } from '../../../constants';
import { de } from 'date-fns/locale';
import { format as formatDate, subDays } from 'date-fns';
import { ampliTrackSubscriptionCancellationSelectedEvent } from '../../../services/track.service';
import { CancellationOfferRedeemed } from '../../../ampli';
import services from '../../../services';
import { NotificationContext } from '../../../state/NotificationContextProvider';
import { getSubscription } from '../../../actions/user.actions';
import useAction from '../../../utils/useAction';

interface DiscountDialogContentProps {
  setStep: (step: DialogStep) => void;
  onClose: () => void;
  subscriptionStartDate: string;
  licenceId: string;
  userId: string;
}

const DiscountDialog: React.FC<DiscountDialogContentProps> = (props) => {
  const { t } = useTranslation();
  const { showNotification } = useContext(NotificationContext);
  const getSubscriptionAction = useAction(getSubscription);
  const { setStep, onClose, subscriptionStartDate, licenceId, userId } = props;
  const { isXsScreen, isSmScreen } = useScreenSize();
  const isMobile = isXsScreen || isSmScreen;
  const [isLoading, setIsLoading] = useState(false);
  const startDate = formatDate(new Date(subscriptionStartDate), subscriptionDateFormat, { locale: de });
  const trialEndDate = formatDate(
    subDays(new Date(subscriptionStartDate), 1),
    subscriptionDateFormat,
    { locale: de }
  );

  const handleDiscount = async () => {
    try {
      setIsLoading(true);
      await services.billing.addTrialDiscount(userId);
      await getSubscriptionAction();
      showNotification({
        message: t('Your new discount is active!'),
        description: t(
          'You will automatically receive a 30% discount on your subscription from {{startDate}}.',
          { startDate }
        ),
        severity: 'success',
      });
    } catch (error) {
      showNotification({
        message: t('An error occurred while applying the discount'),
        severity: 'error',
      });
      notifyBugsnagHandledError(error as Error);
    } finally {
      services.track.ampliEventTrack({
        event: new CancellationOfferRedeemed({
          campaign: campaign.schulstart30,
        })
      });
      onClose();
    }
  };

  const newPrice: Record<string, string> = {
    '2850': t('€6.93 incl. (instead of €9.90 per month)'),
    '2830': t('€13.93 incl. (instead of €19.90 per month)'),
  };
  
  return (
    <>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'flex-start',
            flex: '1 0 0',
            alignSelf: 'stretch',
          }}
        >
          {!isXsScreen && (
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: isSmScreen ? 'center' : 'flex-start',
                '& svg': {
                  width: '400px',
                  height: '300px',
                }
              }}
            >
              <CancelIcon />
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: spaces.lg,
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: spaces.lg,
              flex: '1 0 0',
            }}
          > 
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: spaces.xs,
              }}
            >
              <Typography
                variant='h6'
                sx={{ color: palette.text.primary }}
              >
                {t('Extra discount for you to start the school year: Save 30% now!')}
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: palette.text.secondary }}
              >
                {t(
                  'Stay tuned and secure the best offer of the year: 30% off meinUnterricht Pro – only now ' +
                  'for the start of school!'
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: spaces.sm,
              }}
            >
              <Typography
                variant='subtitle1'
                sx={{ color: palette.text.primary }}
              >
                {t('This is what your offer looks like:')}
              </Typography>
              <Box
                data-testid='discount-dialog-setting-items'
                sx={{
                  display: 'grid',
                  gridTemplateRows: isMobile ? 'repeat(3, auto)' : 'repeat(2, minmax(0, 1fr))',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                  rowGap: spaces.md,
                  columnGap: spaces.md,
                  alignSelf: 'stretch',
                  width: '100%',
                }}
              >
                <SettingItem title={t('Your free trial ends on')}>
                  <Typography data-testid='trial-end-date'>{startDate}</Typography>
                </SettingItem>
                <SettingItem title={t('Your new price')}>
                  <Typography data-testid='new-price'>{newPrice[licenceId]}</Typography>
                </SettingItem>
                <SettingItem
                  title={t('You can continue your trial until')}
                  sx={{
                    gridRow: isMobile ? '3 / span 1' : '2 / span 1',
                    gridColumn: isMobile ? '1 / span 1' : '1 / span 2',
                  }}
                >
                  <Typography data-testid='continue-trial-until'>
                    {t('{{date}} cancel', { date: trialEndDate })}
                  </Typography>
                </SettingItem>
              </Box>
            </Box>
          </Box> 
        </Box>
      </DialogContent>
      <DialogActions
        onBack={() => setStep(manageSubscriptionSteps.manage)}
        primaryButton={{
          text: `30 % ${t('Get a discount')}`,
          color: 'primary',
          variant: 'contained',
          onClick: handleDiscount,
          loading: isLoading,
        }}
        secondaryButton={{
          text: t('Continue Cancellation'),
          variant: 'outlined',
          color: 'error',
          onClick: () => {
            ampliTrackSubscriptionCancellationSelectedEvent(true, '');
            setStep(manageSubscriptionSteps.cancel);
          },
        }}
      />
    </>
  );
};

export default DiscountDialog;

